/**
 * The App's Download action for text it already holds in the browser (JSON, Markdown or CSV). The guest hands bounded
 * text and a safe filename through the existing preview port; the parent decides:
 *   requested  the parent had real browser user activation and asked the browser to download (not "saved")
 *   ready      no activation survived the port: the bytes stay in this frame's parent only, offered as a host button
 *   refused    invalid / oversized / unsafe: nothing is kept
 * Nothing here calls the server; the guest supplies no URL, path, fetch instruction or identity.
 */
export const LOCAL_DOWNLOAD_MAX_BYTES = 1_048_576; // 1 MiB of UTF-8 text per download
export const LOCAL_DOWNLOAD_NAME_MAX = 100;

const TYPES = {
  json: { mime: 'application/json', extension: '.json' },
  markdown: { mime: 'text/markdown', extension: '.md' },
  csv: { mime: 'text/csv', extension: '.csv' },
} as const;
export type LocalDownloadType = keyof typeof TYPES;

export type LocalDownloadFile = { name: string; text: string; type: LocalDownloadType };
/** A ready file kept for exactly one mounted frame (its ticket); a replaced frame can never offer it. */
export type OfferedDownload = LocalDownloadFile & { ticket: string };
export type LocalDownloadRefusal =
  | 'download_unavailable'
  | 'download_input_invalid'
  | 'download_type_unsupported'
  | 'download_name_invalid'
  | 'download_too_large'
  | 'download_not_json';
export type LocalDownloadResult =
  | { status: 'requested' }
  | { status: 'ready' }
  | { status: 'refused'; code: LocalDownloadRefusal };

const NAME = /^[A-Za-z0-9][A-Za-z0-9._ -]*$/;

/** Strict: exactly {name,text,type}; the name is a plain file name for the type; the text is bounded UTF-8. */
export function checkLocalDownload(
  input: unknown,
): { ok: true; file: LocalDownloadFile } | { ok: false; code: LocalDownloadRefusal } {
  const value = input as Record<string, unknown> | null;
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    Object.keys(value).some((key) => !['name', 'text', 'type'].includes(key)) ||
    typeof value.name !== 'string' ||
    typeof value.text !== 'string' ||
    typeof value.type !== 'string'
  )
    return { ok: false, code: 'download_input_invalid' };
  if (!Object.hasOwn(TYPES, value.type)) return { ok: false, code: 'download_type_unsupported' };
  const type = value.type as LocalDownloadType;
  const { name } = value;
  if (
    name.length > LOCAL_DOWNLOAD_NAME_MAX ||
    !NAME.test(name) ||
    name.includes('..') ||
    !name.toLowerCase().endsWith(TYPES[type].extension) ||
    name.length === TYPES[type].extension.length
  )
    return { ok: false, code: 'download_name_invalid' };
  if (new TextEncoder().encode(value.text).length > LOCAL_DOWNLOAD_MAX_BYTES)
    return { ok: false, code: 'download_too_large' };
  if (type === 'json') {
    try {
      JSON.parse(value.text);
    } catch {
      return { ok: false, code: 'download_not_json' };
    }
  }
  return { ok: true, file: { name, text: value.text, type } };
}

export type LocalDownloadHost = {
  /** The parent's REAL browser user activation right now (never a guest-supplied flag). */
  activated(): boolean;
  /** Ask the browser to download (the parent's own helper). */
  download(name: string, text: string, mime: string): void;
  /** Keep this frame's one ready file and show the host Download action; null clears it. */
  offer(file: LocalDownloadFile | null): void;
};

/** One local-download request from the guest. A refusal keeps nothing; a request clears any offer. */
export function handleLocalDownload(input: unknown, host: LocalDownloadHost): LocalDownloadResult {
  const checked = checkLocalDownload(input);
  if (!checked.ok) return { status: 'refused', code: checked.code };
  const { file } = checked;
  if (host.activated()) {
    host.offer(null);
    host.download(file.name, file.text, TYPES[file.type].mime);
    return { status: 'requested' };
  }
  host.offer(file);
  return { status: 'ready' };
}

export const localDownloadMime = (type: LocalDownloadType) => TYPES[type].mime;
